import React from 'react';
import { isUndefined } from 'lodash-es';

import {
  CONFIGURATOR_DESIGN_ART_CATEGORY,
  CONFIGURATOR_DESIGN_ART_MIN_SIZE,
  CONFIGURATOR_DESIGN_CATEGORIES,
  CONFIGURATOR_DESIGN_COLORS_CATEGORY,
  CONFIGURATOR_DESIGN_COLORS_MIN_SIZE,
  CONFIGURATOR_DESIGN_DEFAULT_CATEGORY,
  CONFIGURATOR_DESIGN_EXPANDED_STATE,
  CONFIGURATOR_DESIGN_STEP,
  CONFIGURATOR_DESIGN_THEME_CATEGORY,
  CONFIGURATOR_DESIGN_THEME_MIN_SIZE,
} from '../constants';
import {
  ConfiguratorContextDesignArtOption,
  ConfiguratorContextDesignCategory,
  ConfiguratorContextDesignColorsOption,
  ConfiguratorContextDesignThemeOption,
  ConfiguratorDesignArtClickHandler,
  ConfiguratorDesignArtOption,
  ConfiguratorDesignCategory,
  ConfiguratorDesignCategoryClickHandler,
  ConfiguratorDesignColorsClickHandler,
  ConfiguratorDesignColorsOption,
  ConfiguratorDesignThemeClickHandler,
  ConfiguratorDesignThemeOption,
  ConfiguratorSessionDesignState,
  ConfiguratorSessionState,
  ConfiguratorStep,
  SetSessionCallback,
} from '../types';
import { areArtOptionsEqual, isDesignSessionDataEmpty, reorderArtStyle, reorderColors, reorderThemes } from '../utils';

const CONFIGURATOR_DESIGN_FIRST_CATEGORY = CONFIGURATOR_DESIGN_CATEGORIES[0];
const CONFIGURATOR_DESIGN_LAST_CATEGORY =
  CONFIGURATOR_DESIGN_CATEGORIES[CONFIGURATOR_DESIGN_CATEGORIES.length - 1];

type ArtOptionsFilter = (
  siteSeedId: number | undefined,
  siteSkinId: number | undefined,
) => ConfiguratorDesignArtOption[];

export function useDesignStep(
  configThemeOptions: ConfiguratorDesignThemeOption[] = [],
  configArtOptions: ConfiguratorDesignArtOption[] = [],
  sessionState: ConfiguratorSessionState = {},
  setSession: SetSessionCallback,
  step?: ConfiguratorStep,
) {
  const [category, setCategory] = React.useState<ConfiguratorDesignCategory>(CONFIGURATOR_DESIGN_DEFAULT_CATEGORY);
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>(CONFIGURATOR_DESIGN_EXPANDED_STATE);
  const [themeOptions, setThemeOptions] = React.useState<ConfiguratorDesignThemeOption[]>([]);
  const [colorsOptions, setColorsOptions] = React.useState<ConfiguratorDesignColorsOption[]>([]);
  const [artOptions, setArtOptions] = React.useState<ConfiguratorDesignArtOption[]>([]);

  React.useEffect(() => {
    if (step?.id === CONFIGURATOR_DESIGN_STEP.id && isDesignSessionDataEmpty(sessionState)) {
      const firstTheme = configThemeOptions.length ? configThemeOptions[0] : undefined;
      const firstColors = firstTheme?.site_skins.length ? firstTheme.site_skins[0] : undefined;

      const sseid = firstTheme?.site_seed?.id;
      const sskid = firstColors?.id;

      const firstArt = configArtOptions?.find((artOption) => {
        const compatibility = artOption.art_style_data?.compatibility ?? [];

        if (isUndefined(sseid) || isUndefined(sskid)) {
          return false;
        }

        const exists = compatibility.find((item) => item.site_seed_id === sseid && item.site_skin_id === sskid);

        return !isUndefined(exists);
      });

      if (!isUndefined(firstTheme) && !isUndefined(firstColors) && !isUndefined(firstArt)) {
        const designInitialSession: ConfiguratorSessionDesignState = {
          siteSeedId: firstTheme?.site_seed?.id,
          siteSkinId: firstColors?.id,
          artStyleId: firstArt?.id,
          tokens: firstColors?.tokens,
        };

        setSession(designInitialSession);
      }
    }
  }, [configThemeOptions, configArtOptions, sessionState, setSession, step]);

  // Themes selection and list
  const themeOption = React.useMemo<ConfiguratorDesignThemeOption | undefined>(() => {
    return configThemeOptions.find((seedGroup) => seedGroup.site_seed.id === sessionState.siteSeedId);
  }, [configThemeOptions, sessionState.siteSeedId]);

  React.useEffect(() => {
    if (configThemeOptions.length > 0 && themeOptions.length === 0) {
      setThemeOptions(() => {
        return configThemeOptions.length > CONFIGURATOR_DESIGN_THEME_MIN_SIZE
          ? reorderThemes(themeOption, configThemeOptions)
          : configThemeOptions;
      });
    }
  }, [configThemeOptions, themeOption, themeOptions]);

  // Colors selection and list
  const colorsOption = React.useMemo<ConfiguratorDesignColorsOption | undefined>(() => {
    return themeOption?.site_skins.find((siteSkin) => siteSkin.id === sessionState.siteSkinId);
  }, [themeOption, sessionState.siteSkinId]);

  React.useEffect(() => {
    if (!isUndefined(themeOption)) {
      setColorsOptions(() => {
        return themeOption.site_skins.length > CONFIGURATOR_DESIGN_COLORS_MIN_SIZE
          ? reorderColors(colorsOption, themeOption.site_skins)
          : themeOption.site_skins;
      });
    }
  }, [themeOption, colorsOption]);

  // Art selection and list
  const artOption = React.useMemo<ConfiguratorDesignArtOption | undefined>(() => {
    return configArtOptions.find((artStyle) => artStyle.id === sessionState.artStyleId);
  }, [configArtOptions, sessionState.artStyleId]);

  const getFilteredArtOptions = React.useCallback<ArtOptionsFilter>(
    (siteSeedId, siteSkinId) => {
      return configArtOptions.filter((artOption) => {
        const compatibility = artOption.art_style_data?.compatibility ?? [];

        if (isUndefined(siteSeedId) || isUndefined(siteSkinId)) {
          return true;
        }

        const exists = compatibility.find(
          (item) => item.site_seed_id === siteSeedId && item.site_skin_id === siteSkinId,
        );

        return !isUndefined(exists);
      });
    },
    [configArtOptions],
  );

  React.useEffect(() => {
    const filteredArtOptions = getFilteredArtOptions(themeOption?.site_seed?.id, colorsOption?.id);

    if (!areArtOptionsEqual(artOptions, filteredArtOptions)) {
      setArtOptions(() => {
        return filteredArtOptions.length > CONFIGURATOR_DESIGN_ART_MIN_SIZE
          ? reorderArtStyle(artOption, filteredArtOptions)
          : filteredArtOptions;
      });
    }
  }, [themeOption, colorsOption, artOption, artOptions, getFilteredArtOptions]);

  // Selection handlers for each element
  const handleDesignCategoryClick = React.useCallback<ConfiguratorDesignCategoryClickHandler>((nextCategory) => {
    setCategory(nextCategory);
  }, []);

  // Sub-category cycling within the Design step.
  // Returns true when a sub-category move happened so callers can skip
  // advancing the main step until the user is past the last sub-category.
  const currentCategoryIndex = React.useMemo(
    () => CONFIGURATOR_DESIGN_CATEGORIES.findIndex((c) => c.id === category.id),
    [category.id],
  );

  const nextDesignCategory = React.useMemo(
    () => CONFIGURATOR_DESIGN_CATEGORIES[currentCategoryIndex + 1],
    [currentCategoryIndex],
  );

  const previousDesignCategory = React.useMemo(
    () => CONFIGURATOR_DESIGN_CATEGORIES[currentCategoryIndex - 1],
    [currentCategoryIndex],
  );

  const handleDesignCategoryNext = React.useCallback(() => {
    if (isUndefined(nextDesignCategory)) {
      return false;
    }
    setCategory(nextDesignCategory);
    return true;
  }, [nextDesignCategory]);

  const handleDesignCategoryPrevious = React.useCallback(() => {
    if (isUndefined(previousDesignCategory)) {
      return false;
    }
    setCategory(previousDesignCategory);
    return true;
  }, [previousDesignCategory]);

  const handleDesignCategoryReset = React.useCallback(() => {
    setCategory(CONFIGURATOR_DESIGN_FIRST_CATEGORY);
  }, []);

  const handleDesignCategorySetLast = React.useCallback(() => {
    setCategory(CONFIGURATOR_DESIGN_LAST_CATEGORY);
  }, []);

  const handleDesignThemeClick = React.useCallback<ConfiguratorDesignThemeClickHandler>(
    (nextThemeOption, artOption) => {
      const artOptionsForNextTheme = getFilteredArtOptions(
        nextThemeOption.site_seed?.id,
        nextThemeOption.site_skins?.[0]?.id,
      );

      let newArtOptionID = artOptionsForNextTheme?.[0]?.id;

      if (artOption?.id) {
        // Check if current art option is compatible with the new theme & colors
        const isCompatible = artOptionsForNextTheme.find((art) => art.id === artOption.id);

        if (isCompatible) {
          newArtOptionID = artOption.id;
        }
      }

      setSession({
        siteSeedId: nextThemeOption.site_seed?.id,
        siteSkinId: nextThemeOption.site_skins?.[0]?.id,
        tokens: nextThemeOption.site_skins?.[0]?.tokens,
        artStyleId: newArtOptionID,
      });
    },
    [setSession, getFilteredArtOptions],
  );

  const handleDesignColorsClick = React.useCallback<ConfiguratorDesignColorsClickHandler>(
    (nextColorsOption, themeOption, artOption) => {
      const artOptionsForNextTheme = getFilteredArtOptions(themeOption?.site_seed?.id, nextColorsOption?.id);

      let newArtOptionID = artOptionsForNextTheme?.[0]?.id;

      if (artOption?.id) {
        // Check if current art option is compatible with the new theme & colors
        const isCompatible = artOptionsForNextTheme.find((art) => art.id === artOption.id);

        if (isCompatible) {
          newArtOptionID = artOption.id;
        }
      }

      setSession({
        siteSkinId: nextColorsOption?.id,
        tokens: nextColorsOption?.tokens,
        artStyleId: newArtOptionID,
      });
    },
    [setSession, getFilteredArtOptions],
  );

  const handleDesignArtClick = React.useCallback<ConfiguratorDesignArtClickHandler>(
    (nextArtOption) => {
      setSession({
        artStyleId: nextArtOption.id,
      });
    },
    [setSession],
  );

  // Toggle handlers for each category See All buttons
  const handleThemeToggle = React.useCallback<ConfiguratorDesignCategoryClickHandler>(() => {
    const prevToggleState = expanded[CONFIGURATOR_DESIGN_THEME_CATEGORY.id];

    // Check first if it's open, then close, in order to avoid flickering
    if (prevToggleState) {
      setThemeOptions((currentOptions) => {
        return currentOptions.length > CONFIGURATOR_DESIGN_THEME_MIN_SIZE
          ? reorderThemes(themeOption, currentOptions)
          : currentOptions;
      });
    }

    setExpanded((prevState) => ({
      ...prevState,
      [CONFIGURATOR_DESIGN_THEME_CATEGORY.id]: !prevToggleState,
    }));
  }, [expanded, themeOption]);

  const handleColorsToggle = React.useCallback<ConfiguratorDesignCategoryClickHandler>(() => {
    const prevToggleState = expanded[CONFIGURATOR_DESIGN_COLORS_CATEGORY.id];

    // Check first if it's open, then close, in order to avoid flickering
    if (prevToggleState) {
      setColorsOptions((currentOptions) => {
        return currentOptions.length > CONFIGURATOR_DESIGN_COLORS_MIN_SIZE
          ? reorderColors(colorsOption, currentOptions)
          : currentOptions;
      });
    }

    setExpanded((prevState) => ({
      ...prevState,
      [CONFIGURATOR_DESIGN_COLORS_CATEGORY.id]: !prevToggleState,
    }));
  }, [expanded, colorsOption]);

  const handleArtToggle = React.useCallback<ConfiguratorDesignCategoryClickHandler>(() => {
    const prevToggleState = expanded[CONFIGURATOR_DESIGN_ART_CATEGORY.id];

    // Check first if it's open, then close, in order to avoid flickering
    if (prevToggleState) {
      setArtOptions((currentOptions) => {
        return currentOptions.length > CONFIGURATOR_DESIGN_ART_MIN_SIZE
          ? reorderArtStyle(artOption, currentOptions)
          : currentOptions;
      });
    }

    setExpanded((prevState) => ({
      ...prevState,
      [CONFIGURATOR_DESIGN_ART_CATEGORY.id]: !prevToggleState,
    }));
  }, [expanded, artOption]);

  // useMemos for context elements which extend api element with UI only properties like active and onClick
  const designCategories = React.useMemo<ConfiguratorContextDesignCategory[]>(() => {
    return CONFIGURATOR_DESIGN_CATEGORIES.map((themeCategory) => ({
      ...themeCategory,
      active: themeCategory.id === category.id,
      expanded: expanded[themeCategory.id],
      onClick: () => handleDesignCategoryClick(themeCategory),
    }));
  }, [category, expanded, handleDesignCategoryClick]);

  const designThemeOptions = React.useMemo<ConfiguratorContextDesignThemeOption[]>(() => {
    return themeOptions.map((theme) => ({
      ...theme,
      active: theme.id === themeOption?.id,
      onClick: () => handleDesignThemeClick(theme, artOption),
    }));
  }, [themeOption, artOption, themeOptions, handleDesignThemeClick]);

  const designColorsOptions = React.useMemo<ConfiguratorContextDesignColorsOption[]>(() => {
    return colorsOptions.map((colors) => ({
      ...colors,
      active: colors.id === colorsOption?.id,
      onClick: () => handleDesignColorsClick(colors, themeOption, artOption),
    }));
  }, [themeOption, colorsOption, artOption, colorsOptions, handleDesignColorsClick]);

  const designArtOptions = React.useMemo<ConfiguratorContextDesignArtOption[]>(() => {
    return artOptions.map((art) => ({
      ...art,
      active: art.id === artOption?.id,
      onClick: () => handleDesignArtClick(art),
    }));
  }, [artOption, artOptions, handleDesignArtClick]);

  return React.useMemo(
    () => ({
      designCategory: category,
      designCategories,
      designThemeOption: themeOption,
      designThemeOptions,
      designThemeExpanded: expanded[CONFIGURATOR_DESIGN_THEME_CATEGORY.id],
      onDesignThemeToggle: handleThemeToggle,
      designColorsOption: colorsOption,
      designColorsOptions,
      designColorsExpanded: expanded[CONFIGURATOR_DESIGN_COLORS_CATEGORY.id],
      onDesignColorsToggle: handleColorsToggle,
      designArtOption: artOption,
      designArtOptions,
      designArtExpanded: expanded[CONFIGURATOR_DESIGN_ART_CATEGORY.id],
      onDesignArtToggle: handleArtToggle,
      nextDesignCategory,
      previousDesignCategory,
      onDesignCategoryNext: handleDesignCategoryNext,
      onDesignCategoryPrevious: handleDesignCategoryPrevious,
      resetDesignCategory: handleDesignCategoryReset,
      setDesignCategoryToLast: handleDesignCategorySetLast,
    }),
    [
      expanded,
      category,
      designCategories,
      themeOption,
      designThemeOptions,
      handleThemeToggle,
      colorsOption,
      designColorsOptions,
      handleColorsToggle,
      artOption,
      designArtOptions,
      handleArtToggle,
      nextDesignCategory,
      previousDesignCategory,
      handleDesignCategoryNext,
      handleDesignCategoryPrevious,
      handleDesignCategoryReset,
      handleDesignCategorySetLast,
    ],
  );
}
